import { useMemo, useState, type ReactNode } from 'react';
import type { ListGroup, ListInput, QueryEnvelope } from '../../api/client';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import type { Filters } from '../FilterBar';
import { fieldLabel, fieldOf, type ObjectSummary } from '../format';
import { mergeFilters } from '../list-state';
import { useLoad } from '../useLoad';
import { useAppServices } from '../ViewContext';
import { useCellRenderer, type CellRenderer, type Column } from './cells';
import { InfiniteSentinel } from './InfiniteSentinel';
import { AGGREGATION_LABELS, formatTotal } from './totals';
import { useCollection, type Row } from './useCollection';

/**
 * Regroupement d'une `Table` ou d'une `CardList` (ADR-099) : la lecture « groupes » du serveur —
 * une entrée par valeur, son nombre et ses totaux exacts, sous le même filtre, la même recherche et
 * la même autorisation que la liste — puis, à l'ouverture d'un groupe seulement, ses lignes par la
 * liste filtrée sur sa valeur, en défilement infini. Deux niveaux : un groupe ouvert du premier
 * niveau se regroupe sur le second champ. « Sans valeur » se lit par `exists=false`.
 */

type Totals = NonNullable<ListInput['totals']>;
type TotalLabels = ReadonlyArray<{ field_id: string; aggregation: string; label?: Readonly<Record<string, string>> }>;

export interface GroupedProps {
  type: string;
  object: ObjectSummary | undefined;
  /** Champs de regroupement, un ou deux niveaux. */
  fields: readonly string[];
  collapsed: boolean;
  filter: Filters;
  search: { q?: string; q_fields?: readonly string[] };
  sort: { field: string; direction: 'asc' | 'desc' } | null;
  totals?: Totals;
  /** Libellés déclarés des totaux (conteneur). */
  totalLabels?: TotalLabels;
  pageSize: number;
  refreshKey: number;
  columns: readonly Column[];
  /** Champs montrés en plus des colonnes (carte), pour la lecture des libellés. */
  extraFields: readonly string[];
  /** Rendu des lignes d'un groupe : la Table ou les cartes de la représentation. */
  renderRows: (rows: readonly Row[], render: CellRenderer) => ReactNode;
}

export function GroupedCollection(props: GroupedProps) {
  const services = useAppServices();
  const [field, ...rest] = props.fields;
  const key = JSON.stringify({ type: props.type, field, filter: props.filter, search: props.search, totals: props.totals, refresh: props.refreshKey });
  const [groups, reload] = useLoad<QueryEnvelope<Row[]>>(key, () =>
    services.client.listObjects<Row>(props.type, { filter: props.filter, ...props.search, group_by: field!, ...(props.totals === undefined ? {} : { totals: props.totals }) }),
  );
  // Libellés des valeurs (énumération, référence, entité) : lus comme ceux d'une colonne.
  const values = groups.status === 'ready' ? (groups.value.page?.groups ?? []) : [];
  const pseudo = useMemo(() => values.map((group) => ({ [field!]: group.value }) as Row), [values, field]);
  const render = useCellRenderer(props.object, [], [field!], pseudo, undefined);

  if (groups.status === 'loading') return <p className="ay-group__state">Chargement des groupes…</p>;
  if (groups.status === 'error') {
    return (
      <div role="alert" className="ay-banner ay-banner--danger">
        {groups.error.message}{' '}
        <button type="button" className="ay-button ay-button--secondary" onClick={reload}>
          Réessayer
        </button>
      </div>
    );
  }
  if (values.length === 0) return <p className="ay-group__state">Aucun élément.</p>;
  return (
    <div className="ay-groups" role="list" aria-label={`Groupes par ${fieldLabel(props.object, field!)}`}>
      {values.map((group, index) => (
        <GroupSection key={group.value ?? '∅'} {...props} field={field!} rest={rest} group={group} label={group.value === null ? 'Sans valeur' : render.display(field!, pseudo[index]!)} />
      ))}
      {groups.value.page?.groups_truncated === true && <p className="ay-group__state">Seuls les premiers groupes sont affichés : précisez la recherche ou les filtres.</p>}
    </div>
  );
}

function GroupSection(props: GroupedProps & { field: string; rest: readonly string[]; group: ListGroup; label: string }) {
  const { group, field, object } = props;
  const [open, setOpen] = useState(!props.collapsed);
  const filter = mergeFilters(props.filter, { [field]: group.value === null ? { exists: 'false' } : { eq: group.value } });
  const totals = (group.totals ?? []).map((entry) => {
    const declared = props.totalLabels?.find((item) => item.field_id === entry.field_id && item.aggregation === entry.aggregation);
    const name = fieldLabel(object, entry.field_id);
    const label = declared?.label !== undefined ? text(declared.label) : entry.aggregation === 'sum' ? name : `${AGGREGATION_LABELS[entry.aggregation]} · ${name}`;
    const value = formatTotal(entry, fieldOf(object, entry.field_id));
    return { id: `${entry.aggregation}:${entry.field_id}`, label, value: Array.isArray(value) ? value.join(' · ') : value };
  });
  const id = `group-${field}-${group.value ?? 'none'}`.replace(/[^a-zA-Z0-9_-]/g, '_');
  return (
    <section className={`ay-group${open ? ' ay-group--open' : ''}`} role="listitem" aria-labelledby={`${id}-head`}>
      <h3 className="ay-group__title">
        <button type="button" id={`${id}-head`} className="ay-group__head" aria-expanded={open} aria-controls={`${id}-body`} onClick={() => setOpen(!open)}>
          <Icon name={open ? 'chevron-down' : 'chevron'} size={14} />
          <span className="ay-group__label">{props.label}</span>
          <span className="ay-group__count">{group.count.toLocaleString('fr')}</span>
          {totals.map((total) => (
            <span key={total.id} className="ay-group__total">
              <span className="ay-group__totallabel">{total.label}</span> {total.value}
            </span>
          ))}
        </button>
      </h3>
      {open && (
        <div id={`${id}-body`} className="ay-group__body">
          {props.rest.length > 0 ? <GroupedCollection {...props} fields={props.rest} filter={filter} /> : <GroupRows {...props} filter={filter} />}
        </div>
      )}
    </section>
  );
}

function GroupRows(props: GroupedProps) {
  const collection = useCollection({ type: props.type, input: { filter: props.filter, ...props.search, ...(props.sort === null ? {} : { sort: [props.sort] }) }, pageSize: props.pageSize, dropSortOnError: true }, props.refreshKey);
  const render = useCellRenderer(props.object, props.columns, props.extraFields, collection.rows, collection.meta);
  if (collection.status === 'error' && collection.rows.length === 0) {
    return (
      <div role="alert" className="ay-banner ay-banner--danger">
        {collection.error?.message}{' '}
        <button type="button" className="ay-button ay-button--secondary" onClick={collection.reload}>
          Réessayer
        </button>
      </div>
    );
  }
  if (collection.status === 'loading' && collection.rows.length === 0) return <p className="ay-group__state">Chargement…</p>;
  return (
    <>
      {props.renderRows(collection.rows, render)}
      <InfiniteSentinel loadingMore={collection.loadingMore} done={collection.done} error={collection.moreError} count={collection.total ?? collection.rows.length} onMore={collection.loadMore} />
    </>
  );
}
